/**
 * Pure helpers for the Kanban per-board "tag alerts" feature.
 *
 * A board can be configured so that "when a card gets tag X, email these people".
 * On card save the panel diffs old -> new tags; if a NEWLY-added tag matches the
 * board's configured alert tags, an email is fired. These helpers own the tag
 * normalization + the newly-added-and-matching diff.
 *
 * Intentionally pure — no Vuex, no Firestore, no axios — so they can be unit
 * tested under node:test and reused from both the Add and Edit card panels.
 *
 * Unit tests: src/store/__tests__/kanban-tag-alerts.test.js — that file MIRRORS
 * this logic (webpack src is ESM; node:test can't import it), so if you change a
 * function here you MUST update the mirror there too.
 */

/**
 * Normalize a list of tags to comparable canonical names:
 *   - accepts either [{ name }] tag objects (the card / vue-multiselect shape)
 *     or plain [string] names, mixed freely;
 *   - trims + lowercases each name;
 *   - drops blanks (empty / whitespace-only);
 *   - de-duplicates, preserving first-seen order.
 *
 * @param {Array<{name:string}|string>} tags
 * @returns {string[]} canonical tag names
 */
export function normalizeTagNames(tags) {
  if (!Array.isArray(tags)) return [];
  const seen = new Set();
  const out = [];
  for (const t of tags) {
    const raw = typeof t === 'string'
      ? t
      : (t && typeof t.name === 'string' ? t.name : '');
    const name = raw.trim().toLowerCase();
    if (!name) continue;
    if (seen.has(name)) continue;
    seen.add(name);
    out.push(name);
  }
  return out;
}

/**
 * Return the normalized tag names that are NEWLY added on this save AND are
 * configured as alert tags on the board — i.e. present in newTags, absent from
 * oldTags, and in alertTagNames.
 *
 * Mirrors the Set-diff approach of getNewlyMentionedEmployeeIDs
 * (src/utils/taskMentions.js): "new minus original". This is what keeps editing
 * a card that ALREADY carried an alert tag from re-firing the email — only a
 * freshly-added matching tag returns non-empty.
 *
 * @param {Array<{name:string}|string>} oldTags        tags before the edit ([] for a brand-new card)
 * @param {Array<{name:string}|string>} newTags        tags after the edit
 * @param {Array<{name:string}|string>} alertTagNames  the board's configured alert tags
 * @returns {string[]} normalized names that should fire an alert (deduped)
 */
export function getNewlyAddedAlertTags(oldTags, newTags, alertTagNames) {
  const alerts = new Set(normalizeTagNames(alertTagNames));
  if (alerts.size === 0) return [];
  const before = new Set(normalizeTagNames(oldTags));
  return normalizeTagNames(newTags).filter(
    name => !before.has(name) && alerts.has(name)
  );
}

/**
 * Build the exact POST body for the `/kanban-tag-alert` endpoint, or return null
 * when no alert should fire. This is the shared decision the Add and Edit card
 * panels both make after a save — extracted here so the two can't drift.
 *
 * Returns null (do NOT fire) when the board has no enabled tagAlerts config, or
 * when the save added no NEW tag that is in the board's alert list. Otherwise
 * returns the payload. Recipients are intentionally NOT included — the backend
 * reads them from the board doc.
 *
 * NOTE: this is the pure part only. The per-site EMAIL-capability gate
 * (isEmailEnabled) stays in the panels, which own `this.settings`.
 *
 * @param {object} args
 * @param {{id:string,name?:string,tagAlerts?:object}} args.board
 * @param {string} args.siteID
 * @param {Array<{name:string}|string>} args.oldTags
 * @param {Array<{name:string}|string>} args.newTags
 * @param {string} args.cardName
 * @param {string|number} args.addedByEmployeeID
 * @param {string} [args.addedByName]
 * @returns {object|null} the POST payload, or null when nothing should fire
 */
export function buildTagAlertPayload({ board, siteID, oldTags, newTags, cardName, addedByEmployeeID, addedByName }) {
  const cfg = board && board.tagAlerts;
  if (!cfg || !cfg.enabled) return null;
  const matchedTags = getNewlyAddedAlertTags(oldTags, newTags, cfg.tags || []);
  if (!matchedTags.length) return null;
  return {
    siteID,
    boardID: board.id,
    boardName: board.name || '',
    cardName: cardName || '',
    matchedTags,
    addedByEmployeeID,
    addedByName: addedByName || 'a teammate',
  };
}
